import { useEffect, useMemo, useRef } from 'react';
import { AutoSizer, Collection, WindowScroller } from 'react-virtualized';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useScreenSize from '@/hooks/useScreenSize';
import { resolveLimitedEditionContext, resolveLimitedEditionSizeData } from '@/pages/artwork/content-section/utils/limitedEdition.utils';
import ArtworkCard from '@/shared-components/cards/components/ArtworkCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';

import './styles/VirtualList.css';

function resolveArtworkSlug(artwork) {
  if (!artwork) {
    return null;
  }

  return (
    artwork.slug
    ?? artwork.product_slug
    ?? artwork.artwork_slug
    ?? artwork.artwork?.slug
    ?? artwork.product?.slug
    ?? artwork.artwork_data?.slug
    ?? artwork.product_data?.slug
    ?? artwork.product_id
    ?? null
  );
}

export default function VirtualList({ items, onLoadMore, navigateToArtwork, profileCollection = false }) {
  const screenSize = useScreenSize();
  const columnSizesCache = useRef(initColumnSizesCache());
  const sizeAndPositionCache = useRef([]);
  const collectionRef = useRef();
  const isFirstRender = useIsFirstRender();
  const adminOnlySequenceMap = useMemo(() => {
    const data = items?.data ?? [];
    if (!Array.isArray(data) || data.length === 0) {
      return new Map();
    }

    const sizeGroups = new Map();
    data.forEach((artwork, index) => {
      if (!isRestrictedArtwork(artwork)) {
        return;
      }
      const holdingType = artwork?.metadata?.holding_type ?? artwork?.holding_type ?? null;
      if (holdingType !== 'limited_edition') {
        return;
      }
      const { metadata } = resolveLimitedEditionContext(artwork);
      const { sizeKey } = resolveLimitedEditionSizeData(artwork, { metadata });
      const normalizedKey = typeof sizeKey === 'string' ? sizeKey.toLowerCase() : null;
      const key = normalizedKey || 'edition';
      if (!sizeGroups.has(key)) {
        sizeGroups.set(key, []);
      }
      sizeGroups.get(key).push(index);
    });

    const map = new Map();
    sizeGroups.forEach((indices) => {
      indices.forEach((index, position) => {
        map.set(index, {
          number: position + 1,
          total: indices.length,
        });
      });
    });
    return map;
  }, [items?.data]);

  function initColumnSizesCache() {
    const columnCount = screenSize.width <= 700 ? 2 : 3;
    const spacer = screenSize.width <= 550 ? 10 : 15;
    const widthOfList = Math.max((() => {
      if (screenSize.width >= 1230) return screenSize.width - 120;
      if (screenSize.width > 1000) return screenSize.width - 80;
      return screenSize.width - 22;
    })(), 0);
    const columnWidth = (widthOfList - spacer*(columnCount-1)) / columnCount;

    let columnStarts = [];
    let columnHeights = [];

    for(let i=0; i<columnCount; i++) {
      if(i===0) columnStarts[i] = 0; 
      else columnStarts[i] = columnStarts[i-1] + columnWidth + spacer;
      columnHeights[i] = 0;
    }

    return { columnCount, columnWidth, spacer, columnStarts, columnHeights };
  }

  function resolveProfilePreviewImageSize(columnWidth, aspectRatio) {
    const safeWidth = Number.isFinite(columnWidth) && columnWidth > 0 ? columnWidth : 300;
    const safeAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
    const smallerDimension = safeAspectRatio > 1 ? safeWidth / safeAspectRatio : safeWidth;
    const devicePixelRatio = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const cappedPixelRatio = Math.min(devicePixelRatio, 2);
    return Math.min(Math.ceil(smallerDimension * cappedPixelRatio), 2000);
  }

  function cellSizeAndPositionGetter({ index }) {
    if(!sizeAndPositionCache.current[index]) {
      const collectionItem = items?.data?.[index];
      const aspectRatio =
        collectionItem?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio
        ?? 1;
      const safeAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
      const heightOfImage = columnSizesCache.current.columnWidth / safeAspectRatio;
      const baseHeightOffset = screenSize.width <= 550 ? 92.5 : screenSize.width <= 1004 ? 95.5 : 102;
      const isLimitedEditionHolding = (collectionItem?.metadata?.holding_type ?? collectionItem?.holding_type) === "limited_edition";
      const limitedEditionExtraHeight = isLimitedEditionHolding ? (screenSize.width <= 550 ? 28 : 24) : 0;
      const heightOfItem = heightOfImage + baseHeightOffset + limitedEditionExtraHeight;
      const shortestColumn = columnSizesCache.current.columnHeights.indexOf(Math.min(...columnSizesCache.current.columnHeights));
      const item_x = columnSizesCache.current.columnStarts[shortestColumn];
      const item_y = columnSizesCache.current.columnHeights[shortestColumn];
    
      columnSizesCache.current.columnHeights[shortestColumn] += heightOfItem + columnSizesCache.current.spacer;

      const calculatedSizeAndPosition = {
        width: columnSizesCache.current.columnWidth,
        height: heightOfItem,
        x: item_x,
        y: item_y
      };
      sizeAndPositionCache.current[index] = calculatedSizeAndPosition;
      return calculatedSizeAndPosition;
    } else {
      return sizeAndPositionCache.current[index];
    }
  }

  useEffect(() => {
    if(!isFirstRender) {
      columnSizesCache.current = initColumnSizesCache();
      sizeAndPositionCache.current = [];
      collectionRef.current.recomputeCellSizesAndPositions();
    } 
  }, [screenSize]);

  return (
    <>
      <InfiniteScroll 
        onLoadMore={onLoadMore} 
        shouldLoadMore={items.data.length < items.totalDocs} 
        listLength={items.data.length} 
        className="user-profile-collection__infinite-scroll-wrapper"
        defaultInfiniteLoaderClassName="profile-collection__infinite-scroll-spinner"
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Collection
                    autoHeight
                    width={width === 0 ? 0.1 : width}
                    height={height}
                    scrollTop={scrollTop}
                    ref={collectionRef}
                    cellCount={items.data.length}
                    cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                    className="remove-scrollbar"
                    cellRenderer={({ index, key, style }) => {
                      const artwork = items.data[index];
                      const slug = resolveArtworkSlug(artwork);
                      const handleArtworkClick = () => {
                        if (!slug) {
                          return;
                        }

                        const holdingId = artwork?.holding_id ?? artwork?.product_id ?? null;
                        const ownerCandidate = artwork?.owner ?? artwork?.product_owner ?? null;
                        let ownerId = null;
                        if (typeof ownerCandidate === "string" || typeof ownerCandidate === "number") {
                          ownerId = `${ownerCandidate}`;
                        } else if (ownerCandidate && typeof ownerCandidate === "object") {
                          ownerId = ownerCandidate.id ?? ownerCandidate._id ?? ownerCandidate.owner_id ?? ownerCandidate.ownerId ?? null;
                          ownerId = ownerId ? `${ownerId}` : null;
                        }
                        const holdingType = artwork?.metadata?.holding_type ?? artwork?.holding_type ?? null;

                        if (holdingId && ownerId) {
                          navigateToArtwork(slug, undefined, {
                            query: {
                              view: "holding",
                              holding_id: holdingId,
                              owner_id: ownerId,
                              holding_type: holdingType ?? undefined,
                            },
                            viewContext: {
                              type: "holding",
                              holdingId,
                              ownerId,
                              holdingType,
                            },
                          });
                          return;
                        }

                        navigateToArtwork(slug);
                      };

                      return (
                        <div key={key} style={style} ref={infiniteScrollAssigner(index)}>
                          {(() => {
                            const aspectRatio =
                              artwork?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio;
                            const previewImageSize = resolveProfilePreviewImageSize(
                              columnSizesCache.current.columnWidth,
                              aspectRatio
                            );

                            return (
                              <ArtworkCard
                                artworkData={artwork}
                                onClick={handleArtworkClick}
                                profileCollection={profileCollection}
                                previewImageSize={previewImageSize}
                                limitedEditionSequenceNumber={adminOnlySequenceMap.get(index)?.number ?? null}
                                limitedEditionSequenceTotal={adminOnlySequenceMap.get(index)?.total ?? null}
                              />
                            );
                          })()}
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </>
  );
}
